import {
  DialogPanel,
  Dialog as HUIDialog,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { DisableInert } from '@captaincause/lib/DisableInert'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { Button } from '../Button'
import { tm } from '../tm'
import i18n from './i18n'
import type { ButtonProps } from '../Button'
import type { IconKeys } from '../Icon'
import type { Locales, ProjectId } from '@captaincause/types'
import type { CSSProperties, ReactNode } from 'react'

interface PanelProps {
  children: ReactNode | ((props: { onClose: () => void }) => ReactNode)
  className?: string
  close?: (id: ProjectId | undefined) => void
  onTransitionEnterDone?: () => void
  containerClassName?: string
  panelClassName?: string
  headerClassName?: string
  HeaderCloseButton?: (props: ButtonProps) => ReactNode
  headerCloseIcon?: IconKeys
  headerStyle?: CSSProperties
  isOpen: boolean
  locale: Locales
  orientation: 'right' | 'left' | 'bottom'
  style?: CSSProperties
  rootNode: string
}

const orientationClassNames: Record<PanelProps['orientation'], string> = {
  left: 'absolute left-0 top-0 h-full w-full overflow-y-scroll bg-white md:w-1/2 -translate-x-0 data-[closed]:-translate-x-full',
  right:
    'absolute right-0 top-0 h-full w-full overflow-y-scroll bg-white md:w-1/2 translate-x-0 data-[closed]:translate-x-full',
  bottom:
    'absolute bottom-0 left-0 h-full w-full overflow-y-scroll bg-white translate-y-0 data-[closed]:translate-y-full',
}

const DefaultButton = ({ children, ...props }: ButtonProps) => (
  <Button {...props}>{children}</Button>
)

const emptyObject = {}
const noop = () => {}
function PanelWithRef(
  {
    children,
    className = '',
    onTransitionEnterDone = undefined,
    close = noop,
    containerClassName = '',
    panelClassName = '',
    headerClassName = '',
    HeaderCloseButton = DefaultButton,
    headerCloseIcon = 'drawer-forward',
    headerStyle = emptyObject,
    isOpen,
    locale,
    orientation,
    style = emptyObject,
    rootNode,
  }: PanelProps,
  scrollContainer: any,
) {
  const t = i18n[locale] ?? i18n.fr

  const { track } = useMixpanel()

  const ref = useRef<HTMLBaseElement & { hasListeners: boolean }>(null)

  const [portalsElement, setPortalsElement] = useState<Element | null>(null)

  const handleClose = useCallback(
    ({ bySwipe = false }: { bySwipe?: Boolean } = {}) => {
      track('Close SidePanel', { bySwipe })
      close(undefined)
    },
    [track, close],
  )

  useEffect(() => {
    setPortalsElement(document.getElementById('portals'))
  }, [setPortalsElement])

  useEffect(() => {
    const clickOutside = (event: MouseEvent): void => {
      if (ref?.current && !ref.current.contains(event.target as HTMLElement)) {
        handleClose()
      }
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.code === 'Escape') {
        handleClose()
      }
    }

    const aside = ref.current

    const addListenerAndOverflow = () => {
      if (aside && !aside.hasListeners) {
        document.addEventListener('mouseup', clickOutside)
        document.addEventListener('keydown', onKeyDown)
        aside.hasListeners = true

        document.getElementsByTagName('html')[0].style.overflow = 'hidden'
      }
    }

    const cleanup = () => {
      if (aside?.hasListeners) {
        document.removeEventListener('mouseup', clickOutside)
        document.removeEventListener('keydown', onKeyDown)
        aside.hasListeners = false

        document.getElementsByTagName('html')[0].style.overflow = 'initial'
      }
    }

    if (isOpen) {
      addListenerAndOverflow()
    } else {
      cleanup()
    }

    return cleanup
  }, [isOpen, handleClose])

  //

  const [isDialogOpened, setIsDialogOpened] = useState<false | undefined>(
    undefined,
  )

  const onDialogClose = () => {
    setIsDialogOpened(false)
  }

  const afterLeave = () => {
    setIsDialogOpened(undefined)
    close(undefined)
  }

  const afterEnter = () => {
    onTransitionEnterDone?.()
  }

  useEffect(() => {
    if (isOpen) {
      window.document.documentElement.style.scrollBehavior = 'auto'
    }

    return () => {
      window.document.documentElement.style.scrollBehavior = ''
    }
  }, [isOpen])

  if (!portalsElement) return null

  const orientationClassName = orientationClassNames[orientation]

  return (
    <Transition show={isDialogOpened ?? isOpen}>
      <HUIDialog
        as="div"
        className={tm('relative z-50', className)}
        onClose={onDialogClose}
      >
        <TransitionChild afterEnter={afterEnter} afterLeave={afterLeave}>
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity ease-in-out data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={tm(
              'flex min-h-full items-center justify-center',
              containerClassName,
            )}
          >
            <TransitionChild>
              <DialogPanel
                ref={scrollContainer}
                className={tm(
                  'relative transform-gpu overflow-y-auto bg-white px-6 pb-8 pt-4 align-middle shadow-xl transition-transform duration-300 data-[enter]:ease-in data-[leave]:ease-out',
                  orientationClassName,
                  panelClassName,
                )}
              >
                <div
                  className={tm(
                    'z-10 mb-4 h-auto w-full border-b border-light-gray pb-4 pt-0',
                    headerClassName,
                  )}
                  style={headerStyle}
                >
                  <HeaderCloseButton
                    variant="raw"
                    iconLeft={headerCloseIcon}
                    onClick={() => {
                      onDialogClose()
                    }}
                  >
                    {t.close}
                  </HeaderCloseButton>
                </div>

                {typeof children === 'function'
                  ? children({ onClose: onDialogClose })
                  : children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
        <DisableInert root={rootNode} />
      </HUIDialog>
    </Transition>
  )
}

export const Panel = forwardRef(PanelWithRef)
